import { useCallback, useEffect, useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { topicLabel } from '@gogrov/shared'
import { useAuthStore } from '@/store/auth'
import { useUIStore } from '@/store/ui'
import { useProfile, useUpdatePrefs } from '@/hooks/useProfile'
import { usePills, useDismissSeason } from '@/hooks/useMix'
import { useNotices, useMarkNoticeSeen } from '@/hooks/useAchievements'
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner'
import { AlertPushBanner } from '@/components/alerts/AlertPushBanner'
import { useUnreadAlerts } from '@/hooks/useAlerts'
import { UnlockWatcher } from '@/components/coins/UnlockWatcher'
import { Emma } from '@/components/emma/Emma'
import { EmmaChat } from '@/components/emma/EmmaChat'
import { VoiceRingSheet } from '@/components/voice/VoiceRingSheet'
import { VoiceOffer } from '@/components/voice/VoiceOffer'
import { useLocaleSync } from '@/lib/i18n'
import '@/styles/voice.css'
import '@/styles/l10n-fit.css'
import { type EmmaSkin } from '@/components/emma/emmaEngine'
import { setStatusBarTheme } from '@/lib/statusbar'
import { PhoneTopBar, TabBar } from './chrome/PhoneBars'
import { MeterVoice } from './MeterVoice'
import { DesktopSidebar, DesktopToolbar } from './chrome/DesktopNav'
import { useChrome, setRecPick } from './chrome/chromeStore'
import { RecordPickSheet, recordTodo } from './chrome/RecordPickSheet'
import { useLibraryStories } from '@/hooks/useLibrary'
import { useIsDesktop } from './chrome/useIsDesktop'
import { ROUTES } from './chrome/nav'
import '@/styles/gg.css'
import '@/styles/chrome.css'
import '@/styles/emma.css'

/** GoGrov is dark only (Brian, Oct 14: one palette). Puts the root class and
 *  the theme on the document, so overlays mounted on body (the sprout loader,
 *  dialogs) match the app. Returns the theme for older callers. */
export function useThemeRoot(): 'dark' {
  useEffect(() => {
    const html = document.documentElement
    html.classList.add('gg-root')
    html.dataset.theme = 'dark'
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#18181b')
    void setStatusBarTheme('dark')
    return () => { html.classList.remove('gg-root'); delete html.dataset.theme }
  }, [])
  return 'dark'
}

export function BrandMark() {
  return <img className="gg-brandmark" src="/brand/gogrov-logo-horizontal.svg" alt="GoGrov" width={98} height={26} />
}

/**
 * The app frame (DEVELOPER-HANDOFF §4).
 *
 * Phone: a 96px glass top bar (seeds, initials; the screen's title and
 * actions), the screen, and a matching tab bar (Feed, Library, Record ＋,
 * Alerts, Emma). No menu drawer.
 *
 * Desktop (900px and up): the sidebar (Emma has a row in it, above the seeds:
 * desktop-nav.js v3.1), one 48px toolbar ("Group › Screen" and the screen's
 * actions) over the screen. No phone bars.
 *
 * Each screen declares its title, Back and actions with <PageChrome>.
 */
export function Shell() {
  const theme = useThemeRoot()
  const client = useAuthStore((s) => s.client)
  const viewingAs = useUIStore((s) => s.viewingAs)
  const { data: profile } = useProfile()
  const desktop = useIsDesktop()
  const bars = useChrome((s) => s.bars)
  const displayClient = viewingAs ?? client
  // The Alerts tab's red count (topbar.js: the alerts waiting, "9+" past nine).
  const unread = useUnreadAlerts()
  // Record ＋ (rec-pick.js): its sheet opens over the current screen; the
  // /app/record/pick deep link opens it over the feed, and closing it there
  // leaves the feed. Any other move closes it.
  const recPick = useChrome((s) => s.recPick)
  const location = useLocation()
  const navigate = useNavigate()
  const onPick = location.pathname.replace(/\/$/, '') === ROUTES.recordPick
  useEffect(() => { setRecPick(onPick) }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps
  const closePick = useCallback(() => {
    setRecPick(false)
    if (onPick) navigate(ROUTES.feed, { replace: true })
  }, [onPick, navigate])
  const { stories: libStories } = useLibraryStories()
  const toRecord = recordTodo(libStories).length

  // The account's language (clients.language, once onboarding writes it) sets the strings and <html lang>.
  useLocaleSync((displayClient as { language?: string | null } | null)?.language)

  const emma = (variant: 'tab' | 'dock') => (
    <Emma variant={variant} ring color={profile?.emma_color} skin={(profile?.emma_skin as EmmaSkin | null) ?? 'seed'} accent={displayClient?.accent_color} />
  )

  return (
    <div className={`gg gg-app ${desktop ? 'gg-app--desk' : 'gg-app--phone'}${!desktop && !bars ? ' gg-app--immersive' : ''}`} data-theme={theme}>
      {desktop ? (
        <div className="gg-deskframe">
          <DesktopSidebar emma={emma('dock')} />
          <div className="gg-deskcol">
            <DesktopToolbar />
            <ImpersonationBanner />
            <main className="gg-main"><Outlet /></main>
          </div>
        </div>
      ) : (
        <>
          {bars && <PhoneTopBar />}
          <ImpersonationBanner />
          <main className="gg-main"><Outlet /></main>
          {bars && <TabBar emma={emma('tab')} alerts={unread} toRecord={toRecord} />}
        </>
      )}

      <RecordPickSheet open={recPick} onClose={closePick} />
      <EmmaChat />
      <VoiceRingSheet />
      <VoiceOffer desktop={desktop} />
      <AlertPushBanner />
      <UnlockWatcher />
      <MeterVoice emmaShown={desktop || bars} />
      <NoticeWatcher />
      <SeasonWatcher />
      <Splash />
    </div>
  )
}

/** Medal handovers, near misses and anniversary gifts, one at a time. */
function NoticeWatcher() {
  const { data: notices } = useNotices()
  const mark = useMarkNoticeSeen()
  const next = notices?.[0]
  if (!next) return null
  return (
    <div className="gg-toast" role="status">
      <span className="eyebrow eyebrow--accent">
        {next.kind === 'anniversary' ? 'Deep Roots' : next.kind === 'medal_near' ? 'So close' : 'Medals'}
      </span>
      <strong>{next.title}</strong>
      <p>{next.body}</p>
      <div className="gg-toast__row">
        {next.medal_id && <Link className="gg-btn gg-btn--sm" to={ROUTES.achievements} onClick={() => mark.mutate(next.id)}>See the case</Link>}
        <button className="gg-btn gg-btn--sm gg-btn--amber" onClick={() => mark.mutate(next.id)}>Got it</button>
      </div>
    </div>
  )
}

const DAY = 24 * 60 * 60 * 1000

/**
 * Emma speaks up about seasons once, wherever the user is: when a keyword first
 * comes into season, and when one is closing. Three ways out: go there now,
 * hear about it again in a few days, or leave it be.
 */
function SeasonWatcher() {
  const { data: profile } = useProfile()
  const { data: pills } = usePills()
  const { data: notices } = useNotices()
  const updatePrefs = useUpdatePrefs()
  const dismiss = useDismissSeason()
  const navigate = useNavigate()
  const location = useLocation()
  const [hidden, setHidden] = useState(false)
  if (hidden || !profile || !pills || (notices?.length ?? 0) > 0) return null
  if (location.pathname.startsWith('/app/settings') || location.pathname.startsWith(ROUTES.topics)) return null
  if (profile.season_snooze_until && new Date(profile.season_snooze_until).getTime() > Date.now()) return null

  const live = pills.filter((p) => p.is_seasonal && !p.season_dismissed)
  const closing = live.find((p) => p.season === 'closing' && (p.successors?.length ?? 0) > 0)
  const opening = !profile.season_intro_seen_at ? live.find((p) => p.season === 'in') : undefined
  const pill = closing ?? opening
  if (!pill) return null

  const snooze = (ms: number) => updatePrefs.mutate({ season_snooze_until: new Date(Date.now() + ms).toISOString() })
  function go() {
    setHidden(true)
    if (closing) snooze(DAY)
    else updatePrefs.mutate({ season_intro_seen_at: new Date().toISOString() })
    navigate(`${ROUTES.topics}?pill=${pill!.id}`)
  }
  function later() { setHidden(true); snooze(3 * DAY) }
  function never() {
    setHidden(true)
    if (closing) dismiss.mutate({ userId: profile!.id, topic: pill!.topic, termNorm: pill!.term_norm })
    else updatePrefs.mutate({ season_intro_seen_at: new Date().toISOString() })
  }

  return (
    <div className="gg-toast" role="status">
      <span className="eyebrow eyebrow--accent">A note from Emma</span>
      {closing ? (
        <>
          <strong>{closing.term} is closing for the year</strong>
          <p>
            Stories on it will thin out from here. Next usually comes {closing.successors!.slice(0, 2).join(' or ')}.
            You can point that keyword at what is coming, retire it, or leave it as it is. Nothing changes unless you say so.
          </p>
        </>
      ) : (
        <>
          <strong>{pill.term} is in season</strong>
          <p>
            Expect more {topicLabel(pill.topic)} stories on it for a while, so I have lit it up in your keywords.
            When a season starts to close I will tell you, and suggest what usually comes next.
          </p>
        </>
      )}
      <div className="gg-toast__row">
        <button className="gg-btn gg-btn--sm gg-btn--quiet" onClick={never}>No thanks</button>
        <button className="gg-btn gg-btn--sm" onClick={later}>Remind me later</button>
        <button className="gg-btn gg-btn--sm gg-btn--amber" onClick={go}>Take me there now</button>
      </div>
    </div>
  )
}

/** The launch splash, once per session (Brian's splash.js v2): the stacked
 *  GO/grov mark fades in, then "GO Grow your voice." a beat later; it lifts
 *  after 1.4s, a tap skips it, and reduced motion shows it still. */
function Splash() {
  const [phase, setPhase] = useState<'on' | 'in' | 'out' | 'gone'>(() => {
    try { return sessionStorage.getItem('gg_splash') ? 'gone' : 'on' } catch { return 'gone' }
  })
  useEffect(() => {
    if (phase !== 'on') return
    try { sessionStorage.setItem('gg_splash', '1') } catch { /* private mode */ }
    const r = window.requestAnimationFrame(() => window.requestAnimationFrame(() => setPhase((p) => (p === 'on' ? 'in' : p))))
    const a = window.setTimeout(() => setPhase((p) => (p === 'gone' ? p : 'out')), 1400)
    return () => { window.cancelAnimationFrame(r); window.clearTimeout(a) }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (phase !== 'out') return
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const b = window.setTimeout(() => setPhase('gone'), reduce ? 0 : 460)
    return () => window.clearTimeout(b)
  }, [phase])
  if (phase === 'gone') return null
  return (
    <div className={`gg-splash${phase === 'in' ? ' is-in' : ''}${phase === 'out' ? ' is-in is-out' : ''}`} aria-hidden="true" onClick={() => setPhase('out')}>
      <div className="gg-splash__in">
        <img className="gg-splash__mark" src="/brand/gogrov-logo-stacked.svg" alt="" width={188} />
        <p className="gg-splash__line">GO Grow your voice.</p>
      </div>
    </div>
  )
}
